import React, { useEffect, useContext, useRef, useState, useCallback, useMemo } from 'react';
import { GlobalDataContext, TemplateContext, evaluateTemplateValueCollection } from "@ea-lab/reactive-json";
import { Link } from 'react-router-dom';
import set from 'lodash/set';
import { Archive, RotateCcw, Search, Settings, X, LayoutDashboard } from 'lucide-react';
import { archiveProject, unarchiveEntity, fetchCollection } from '../../services/api';
import { projectsProcessor } from '../../dataProcessors/decoupledApiProcessors';
import ConfirmTrashModal from '../shared/ui/ConfirmTrashModal';
import ProjectSettingsModal from './ProjectSettingsModal';
import {
  buildProjectSettingsPatch,
  postProjectSettings,
  projectSaveErrorMessage,
} from '../../services/projectSettings';
import { Picto } from '../shared/ui/PictoIcon';
import { DEFAULT_PICTO } from '../shared/ui/picto';
// Import direct (pas le baril `../shell`) : OrbitChrome y monte tout le shell.
import { FavoriteToggle } from '../shell/FavoriteToggle';
import { useAuth } from '../../context/AuthContext';
import { readShowArchived, writeShowArchived, formatArchiveDate, archiveTimestamp, localDayStart } from '../../services/archive';
import { FAVORITES_EVENT } from '../../services/favorites';

const BASE_SUBMISSION_URL = "/decoupled-api/save?display=decoupled&entity_type=group&bundle=project";

const INITIAL_SYNCABLE_OBJECT = {
  submission_url: BASE_SUBMISSION_URL,
  data: { name: "", account: "", description: "", methodology: "", templateId: "", picto: DEFAULT_PICTO },
  status: null,
  errors: null,
};

const FR_DATE_OPTIONS = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('fr-FR', FR_DATE_OPTIONS);
}

/** Minuscules sans accents : « Rétro » et « retro » doivent se trouver l'un l'autre. */
function normalizeText(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

// Colonnes triables, dans l'ordre de rendu : les <th> sont générés depuis cette
// table, une colonne et sa clé de tri ne peuvent donc pas diverger.
// `defaultDir` = sens du PREMIER clic : une date part du plus récent, un texte de A→Z.
const SORT_COLUMNS = [
  { key: 'name', label: 'Projet', type: 'text', defaultDir: 'asc' },
  { key: 'methodology', label: 'Méthodologie', type: 'text', defaultDir: 'asc' },
  { key: 'updatedAt', label: 'Dernière modification', type: 'date', defaultDir: 'desc' },
];

// Tri par défaut : les projets modifiés le plus récemment en tête.
const DEFAULT_SORT = { key: 'updatedAt', dir: 'desc' };

/** `updatedAt` arrive en 'Y-m-d\TH:i:s' ; une date illisible vaut 0 (donc triée en bas). */
function dateValue(value) {
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function sortValue(project, key, getMethodologyLabel) {
  switch (key) {
    case 'name': return project.name ?? '';
    case 'methodology': return getMethodologyLabel(project.methodology);
    case 'updatedAt': return dateValue(project.updatedAt);
    default: return '';
  }
}

function compareProjects(a, b, sort, getMethodologyLabel) {
  const column = SORT_COLUMNS.find(c => c.key === sort.key);
  const dir = sort.dir === 'desc' ? -1 : 1;
  const va = sortValue(a, sort.key, getMethodologyLabel);
  const vb = sortValue(b, sort.key, getMethodologyLabel);
  if (column?.type === 'date') {
    // Une ligne sans date reste en bas dans LES DEUX sens : un bloc de dates
    // inconnues en tête n'apprend rien.
    if (!va !== !vb) return va ? -1 : 1;
    return dir * (va - vb);
  }
  const sa = String(va || '');
  const sb = String(vb || '');
  if (!sa !== !sb) return sa ? -1 : 1;
  return dir * sa.localeCompare(sb, 'fr', { numeric: true, sensitivity: 'base' });
}

// En-tête triable : la cellule porte un vrai <button> (opérable au clavier),
// aria-sort porte l'état pour les technologies d'assistance, le chevron pour
// l'affichage.
const SortableHeader = ({ column, sort, onSort }) => {
  const active = sort.key === column.key;
  return (
    <th
      scope="col"
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider"
    >
      <button
        type="button"
        onClick={() => onSort(column.key)}
        className={`group inline-flex items-center gap-1 uppercase tracking-wider ${active ? 'text-iris-700' : 'hover:text-slate-700'}`}
        title={`Trier par ${column.label}`}
      >
        {column.label}
        {active ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {sort.dir === 'asc' ? <polyline points="18 15 12 9 6 15" /> : <polyline points="6 9 12 15 18 9" />}
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-slate-300 group-hover:text-slate-400">
            <polyline points="7 15 12 20 17 15" /><polyline points="7 9 12 4 17 9" />
          </svg>
        )}
      </button>
    </th>
  );
};

// ★ d'une ligne : le MÊME composant que l'étoile de l'en-tête, simplement visé
// sur une adresse précise. Un projet en a deux — son tableau de tâches et son
// dashboard sont deux pages, et on épingle celle qu'on ouvre vraiment.
//
// Aucun état de favori ici : l'étoile lit et écrit le magasin de signets de la
// page (`~~.favs`, alimenté par /api/user/prefs, cf. projets.yaml), enregistre
// elle-même et rend le libellé au menu. Le libellé sert aussi de nom accessible
// au bouton : il DOIT distinguer les deux étoiles de la ligne.
const RowFavorite = ({ url, label }) => (
  <FavoriteToggle props={{ dataLocation: '~~.favs', url, label, variant: 'inline' }} />
);

const ProjectList = ({
  initialProjects = "~~.projects",
  initialUsers = "~~.users",
  initialMethodologyOptions = "~~.methodologyOptions",
  initialNewProjectStatus = "~~.newProject.status",
  // Types de tâches BRUTS (items { data: { id, name, parentId, workflow } }) :
  // la pop-in de configuration en a besoin pour cocher les types activés et
  // pour résoudre les noms cochés en identifiants de termes à l'enregistrement.
  initialTaskTypes = "~~.taskTypes",
  // Clients : la pop-in de configuration en fait un <select>. La création d'un
  // client à la volée reste au formulaire « Nouveau Projet » (CreatableSelectField).
  initialAccountOptions = "~~.accountOptions",
}) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const { isAdmin } = useAuth();

  const globalProjects = evaluateTemplateValueCollection({
    valueToEvaluate: initialProjects,
    globalDataContext,
    templateContext
  }) || [];

  const [projects, setProjects] = useState(globalProjects);

  // L'empreinte doit changer quand le CONTENU change, pas seulement quand un
  // projet apparaît ou disparaît : sur les seuls identifiants, une modification
  // (renommage, domaine ajouté…) la laisse intacte, `setProjects` ne rejoue pas
  // et la liste reste périmée jusqu'à un rechargement complet. `updatedAt` bouge
  // à chaque enregistrement et coûte bien moins à comparer que la collection.
  const globalProjectsFingerprint = useMemo(
    () => globalProjects.map(p => `${p.id}:${p.updatedAt ?? ''}`).join(','),
    [globalProjects],
  );

  useEffect(() => {
    setProjects(globalProjects);
  }, [globalProjectsFingerprint]);

  const users = evaluateTemplateValueCollection({
    valueToEvaluate: initialUsers,
    globalDataContext,
    templateContext
  }) || [];

  const methodologyOptions = evaluateTemplateValueCollection({
    valueToEvaluate: initialMethodologyOptions,
    globalDataContext,
    templateContext
  }) || [];

  const accountOptions = evaluateTemplateValueCollection({
    valueToEvaluate: initialAccountOptions,
    globalDataContext,
    templateContext
  }) || [];

  const taskTypes = evaluateTemplateValueCollection({
    valueToEvaluate: initialTaskTypes,
    globalDataContext,
    templateContext
  }) || [];

  const newProjectStatus = evaluateTemplateValueCollection({
    valueToEvaluate: initialNewProjectStatus,
    globalDataContext,
    templateContext
  });

  const prevStatusRef = useRef(newProjectStatus);

  const getMethodologyLabel = useCallback((methodologyId) => {
    if (!methodologyId) return '';
    const option = methodologyOptions.find(o => String(o.value) === String(methodologyId));
    return option ? option.label : methodologyId;
  }, [methodologyOptions]);

  const updateGlobalData = (value, path) => {
    if (globalDataContext.updateData) {
      globalDataContext.updateData(value, path);
    } else {
      const newData = { ...globalDataContext.data };
      set(newData, path, value);
      if (globalDataContext.setData) {
        globalDataContext.setData(newData);
      }
    }
  };

  const updateGlobalProjects = (newProjects) => {
    let path = 'projects';
    if (typeof initialProjects === 'string') {
      path = initialProjects.replace(/^~+\./, '');
    }
    updateGlobalData(newProjects, path);
  };

  const refetchProjects = async () => {
    try {
      // Les templates de projet (groupes `is_template`) ne sont pas des projets.
      const url = '/decoupled-api/group/project/collection?display=decoupled&filter[0][f]=is_template&filter[0][v]=1&filter[0][c]=%3C%3E';
      const raw = await fetchCollection(url, true);
      const processed = projectsProcessor({
        requestContext: { url },
        responseContext: { status: 200 },
        dataToProcess: raw,
      });
      updateGlobalProjects(Array.isArray(processed) ? processed : []);
    } catch (error) {
      console.error('Error refetching projects:', error);
    }
  };

  useEffect(() => {
    const wasSuccess = prevStatusRef.current?.type === 'success';
    const isSuccess = newProjectStatus?.type === 'success';
    prevStatusRef.current = newProjectStatus;

    if (isSuccess && !wasSuccess) {
      refetchProjects();
      setTimeout(() => {
        updateGlobalData(false, 'showCreateModal');
        updateGlobalData(false, 'isEditing');
        updateGlobalData(INITIAL_SYNCABLE_OBJECT, 'newProject');
      }, 800);
    }
  }, [newProjectStatus]);

  const handleOpenCreateModal = () => {
    const initial = {
      ...INITIAL_SYNCABLE_OBJECT,
      data: { ...INITIAL_SYNCABLE_OBJECT.data },
    };
    // Pas de méthodologie pré-sélectionnée : le champ doit rester VIDE pour que
    // le template choisi puisse poser la sienne (le back ne sème que les champs
    // vides, cf. orbit_group_group_presave).
    updateGlobalData(initial, 'newProject');
    updateGlobalData(false, 'isEditing');
    updateGlobalData(true, 'showCreateModal');
  };

  // Les étoiles lisent le magasin de la page en accès NON RÉACTIF : seuls les
  // éléments YAML de reactive-json y sont abonnés, un composant React ne l'est
  // pas. Elles émettent `favoriteschanged` une fois l'écriture faite (l'event
  // remonte jusqu'à window) — c'est le signal qui redemande un rendu ici, sans
  // quoi l'étoile resterait éteinte jusqu'au prochain clic sur la page.
  // (Le magasin initial, lui, arrive avant le 1er rendu : la source
  // /api/user/prefs est `blocking` dans projets.yaml.)
  const [, bumpFavorites] = useState(0);
  useEffect(() => {
    const onFavoritesChanged = () => bumpFavorites((n) => n + 1);
    window.addEventListener(FAVORITES_EVENT, onFavoritesChanged);
    return () => window.removeEventListener(FAVORITES_EVENT, onFavoritesChanged);
  }, []);

  const [showArchived, setShowArchived] = useState(readShowArchived);
  const [archivedProjects, setArchivedProjects] = useState([]);
  // T07: filter archived items by archive date ("archivés depuis le …").
  const [archivedSince, setArchivedSince] = useState('');
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [pendingUnarchive, setPendingUnarchive] = useState(null);
  const [unarchiving, setUnarchiving] = useState(false);

  const [pendingArchive, setPendingArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);

  // Recherche plein texte côté client : la collection est déjà entièrement
  // chargée, donc on filtre en JS plutôt que de refaire un aller-retour API.
  // Insensible à la casse ET aux accents ; tous les mots saisis doivent matcher
  // (ET), sur nom + description + méthodologie.
  const [search, setSearch] = useState('');

  const matchesSearch = useCallback((project) => {
    const words = normalizeText(search).split(/\s+/).filter(Boolean);
    if (words.length === 0) return true;
    const haystack = normalizeText([
      project.name,
      project.description,
      getMethodologyLabel(project.methodology),
    ].join(' '));
    return words.every((word) => haystack.includes(word));
  }, [search, getMethodologyLabel]);

  const [sort, setSort] = useState(DEFAULT_SORT);

  // Un clic sur la colonne active inverse le sens ; sur une autre colonne, on
  // repart du sens naturel de son type.
  const handleSort = (key) => setSort(prev => (
    prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: SORT_COLUMNS.find(c => c.key === key)?.defaultDir || 'asc' }
  ));

  const sortProjects = useCallback(
    (list) => list.slice().sort((a, b) => compareProjects(a, b, sort, getMethodologyLabel)),
    [sort, getMethodologyLabel],
  );

  const visibleProjects = useMemo(
    () => sortProjects(projects.filter(matchesSearch)),
    [projects, matchesSearch, sortProjects],
  );

  const visibleArchivedProjects = useMemo(
    () => archivedProjects
      .filter((project) => {
        if (!archivedSince) return true;
        const at = archiveTimestamp(project.archived);
        // Fail OPEN on an unreadable date: hiding it is what the previous NaN
        // comparison did, and it emptied the list without a word. A visible row
        // with no date is debuggable; a silent disappearance is not.
        if (Number.isNaN(at)) return true;
        return at >= localDayStart(archivedSince);
      })
      .filter(matchesSearch),
    [archivedProjects, archivedSince, matchesSearch],
  );

  // Les archivés forment un second bloc sous les actifs : ils suivent le même
  // tri, sinon les deux moitiés du tableau ne se lisent pas de la même façon.
  const sortedArchivedProjects = useMemo(
    () => sortProjects(visibleArchivedProjects),
    [visibleArchivedProjects, sortProjects],
  );

  const handleArchiveClick = (project) => {
    setPendingArchive(project);
  };

  const handleArchiveConfirm = useCallback(async () => {
    if (!pendingArchive || archiving) return;
    setArchiving(true);
    try {
      await archiveProject(pendingArchive.id);
      const archivedId = pendingArchive.id;
      window.dispatchEvent(new CustomEvent('project:entity-archived', {
        detail: { type: 'project', id: archivedId },
      }));
      setProjects(prev => prev.filter(p => p.id !== archivedId));
      refetchProjects();
    } catch (error) {
      console.error('Error archiving project:', error);
      const msg = error.message?.includes('403')
        ? 'Permission refusée. Vous n\'avez pas les droits pour archiver ce projet.'
        : `Erreur : ${error.message}`;
      alert(msg);
    } finally {
      setArchiving(false);
      setPendingArchive(null);
    }
  }, [pendingArchive, archiving]);

  const fetchArchivedProjects = useCallback(async () => {
    setLoadingArchived(true);
    try {
      const url = '/decoupled-api/group/project/collection?display=decoupled&include_archived=1&filter[0][f]=is_template&filter[0][v]=1&filter[0][c]=%3C%3E';
      const raw = await fetchCollection(url, true);
      const processed = projectsProcessor({
        requestContext: { url },
        responseContext: { status: 200 },
        dataToProcess: raw,
      });
      const all = Array.isArray(processed) ? processed : [];
      const activeIds = new Set(projects.map(p => p.id));
      setArchivedProjects(all.filter(p => p.archived && !activeIds.has(p.id)));
    } catch (err) {
      console.error('Error fetching archived projects:', err);
    } finally {
      setLoadingArchived(false);
    }
  }, [projects]);

  useEffect(() => {
    if (showArchived) fetchArchivedProjects();
    else setArchivedProjects([]);
  }, [showArchived]);

  const handleToggleShowArchived = useCallback(() => {
    setShowArchived(prev => {
      const next = !prev;
      writeShowArchived(next);
      return next;
    });
  }, []);

  // Configuration du projet — LA MÊME pop-in que l'en-tête du tableau kanban
  // (`ProjectSettingsModal`, onglets Types de tâches / Matrice de droits /
  // Général / Utilisateurs). Elle s'ouvre ici sur la ligne, sans passer par le
  // tableau du projet. Le crayon voisin garde le formulaire de création-édition
  // (client, méthodologie, domaines), que cette pop-in ne couvre pas.
  const [settingsProject, setSettingsProject] = useState(null);

  // `settings` est une DÉPENDANCE D'EFFET de la pop-in : un objet recréé à
  // chaque rendu de la liste y rejouerait l'initialisation du formulaire et
  // effacerait la saisie en cours, sans la moindre erreur.
  const settingsModalSettings = useMemo(() => ({
    enabledTaskTypes: settingsProject?.enabledTaskTypes || {},
    workingHours: settingsProject?.workingHours || {},
  }), [settingsProject]);

  const handleSettingsSave = useCallback(async (newSettings) => {
    const project = settingsProject;
    if (!project) return;
    // Le diff est partagé avec le kanban (services/projectSettings.js) : deux
    // copies finiraient par n'écrire pas les mêmes champs, sans rien signaler.
    const patchData = buildProjectSettingsPatch({
      current: {
        name: project.name,
        description: project.description,
        picto: project.picto,
        methodology: project.methodology,
        // `client` côté liste, `account` côté API : le service parle la langue
        // de l'API, le mapper celle de l'écran.
        account: project.client,
        hosts: project.hosts,
        agentInstance: project.agentInstance,
        projectSettings: project.projectSettings,
        enabledTaskTypes: project.enabledTaskTypes,
        workingHours: project.workingHours,
      },
      next: newSettings,
      taskTypes,
    });
    if (Object.keys(patchData).length === 0) return;
    try {
      const resp = await postProjectSettings(project.id, patchData);
      if (!resp.ok) {
        alert(projectSaveErrorMessage(resp.status));
        return;
      }
      // La ligne affiche le nom, la description et le picto : on relit la
      // collection plutôt que de rapiécer l'état local, l'empreinte
      // `id:updatedAt` de la liste rejoue alors le rendu.
      refetchProjects();
    } catch (error) {
      console.error('Project settings save failed', error);
      alert("L'enregistrement de la configuration a échoué.");
    }
  }, [settingsProject, taskTypes]);

  const handleUnarchiveClick = useCallback((project) => {
    setPendingUnarchive(project);
  }, []);

  const handleUnarchiveConfirm = useCallback(async () => {
    if (!pendingUnarchive || unarchiving) return;
    setUnarchiving(true);
    try {
      await unarchiveEntity('group', 'project', pendingUnarchive.id);
      const id = pendingUnarchive.id;
      setArchivedProjects(prev => prev.filter(p => p.id !== id));
      refetchProjects();
    } catch (error) {
      console.error('Error unarchiving project:', error);
      alert(`Erreur : ${error.message}`);
    } finally {
      setUnarchiving(false);
      setPendingUnarchive(null);
    }
  }, [pendingUnarchive, unarchiving]);

  return (
    <div className="min-h-screen bg-slate-50">
      
      {/* Header */}
      <div className="bg-white text-slate-900 px-8 py-4 flex items-center gap-6 shadow-sm border-b border-slate-200">
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">Projets</h1>
          <p className="text-slate-500 text-sm mt-0.5">Gérez vos projets et créez-en de nouveaux.</p>
        </div>
        <button
          type="button"
          onClick={handleToggleShowArchived}
          aria-pressed={showArchived}
          className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${showArchived ? 'text-iris-700 bg-iris-50 hover:bg-iris-100' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
          title={showArchived ? 'Masquer les archivés' : 'Voir les archivés'}
        >
          <Archive size={16} />
          <span>{showArchived ? 'Masquer les archivés' : 'Voir les archivés'}</span>
        </button>
        {showArchived && (
          <label className="flex items-center gap-1.5 text-sm text-slate-500" title="N'afficher que les éléments archivés à partir de cette date">
            <span>Archivés depuis</span>
            <input
              type="date"
              value={archivedSince}
              onChange={(e) => setArchivedSince(e.target.value)}
              className="input text-sm w-auto"
            />
            {archivedSince && (
              <button type="button" onClick={() => setArchivedSince('')} className="text-slate-400 hover:text-slate-700" title="Effacer le filtre">×</button>
            )}
          </label>
        )}
        <button
          onClick={handleOpenCreateModal}
          className="btn btn-primary"
        >
          <span className="text-lg leading-none font-bold">+</span>
          Nouveau Projet
        </button>
      </div>

      <div className="max-w-7xl mx-auto p-8">
        {/* Project List */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Barre d'outils en tête de tableau : la recherche agit sur les lignes
              juste en dessous, elle vit donc dans la même carte qu'elles. */}
          <div className="flex items-center gap-3 px-6 py-3 border-b border-slate-200">
            <div className="relative w-full max-w-sm">
              <span className="absolute inset-y-0 left-3 flex items-center text-slate-400 pointer-events-none">
                <Search size={16} aria-hidden="true" />
              </span>
              <input
                id="project-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un projet…"
                aria-label="Rechercher un projet par nom, description ou méthodologie"
                className="input pl-9 pr-9"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute inset-y-0 right-2 flex items-center text-slate-400 hover:text-slate-700"
                >
                  <X size={14} aria-hidden="true" />
                  <span className="sr-only">Effacer la recherche</span>
                </button>
              )}
            </div>
            {/* Compteur visible ET annoncé : filtrer la table ne produit sinon
                aucun retour perceptible. */}
            <p className="text-sm text-slate-500" role="status" aria-live="polite">
              {search
                ? `${visibleProjects.length + visibleArchivedProjects.length} projet(s) trouvé(s)`
                : ''}
            </p>
          </div>
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                {SORT_COLUMNS.map(column => (
                  <SortableHeader key={column.key} column={column} sort={sort} onSort={handleSort} />
                ))}
                <th scope="col" className="px-6 py-4 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {visibleProjects.map((project) => {
                const methodologyLabel = getMethodologyLabel(project.methodology);
                return (
                <tr key={project.id} className="hover:bg-slate-50 transition-colors group">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-iris-100 rounded-lg flex items-center justify-center text-xl overflow-hidden">
                        {project.pictoImage ? <img src={project.pictoImage} alt="" className="h-full w-full object-cover" /> : <Picto name={project.picto} size={22} />}
                      </div>
                      <div className="ml-4">
                        {/* Chaque destination porte SON étoile, juste après son
                            lien : on épingle le tableau du projet, son dashboard,
                            ou les deux — jamais « le projet » en général, puisque
                            le signet est une adresse. */}
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-0.5">
                            <Link to={`/kanban2/${project.id}`} className="text-sm font-semibold text-slate-900 hover:text-iris-600 hover:underline">
                              {project.name}
                            </Link>
                            <RowFavorite url={`/kanban2/${project.id}`} label={project.name} />
                          </span>
                          {/* Le titre ouvre le tableau ; le dashboard (détail du
                              projet) reste accessible par ce lien secondaire. */}
                          <span className="inline-flex items-center gap-0.5">
                            <Link
                              to={`/projets/${project.id}`}
                              aria-label={`Dashboard du projet ${project.name}`}
                              title={`Dashboard du projet ${project.name}`}
                              className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-iris-600 hover:underline"
                            >
                              <LayoutDashboard size={14} aria-hidden="true" />
                              <span>Dashboard</span>
                            </Link>
                            <RowFavorite url={`/projets/${project.id}`} label={`${project.name} — Dashboard`} />
                          </span>
                        </div>
                        <div className="text-sm text-slate-500 truncate max-w-xs">{project.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`badge ${
                      methodologyLabel === 'Scrum'
                        ? 'badge-info'
                        : 'badge-success'
                    }`}>
                      {methodologyLabel}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                    {formatDate(project.updatedAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-1">
                      {/* Même pop-in que dans le tableau kanban — elle a remplacé
                          le crayon (formulaire de création réemployé en édition),
                          dont elle porte désormais tous les champs. Le libellé
                          porte le nom du projet : répété à l'identique sur chaque
                          ligne, il ne distinguerait rien pour un lecteur d'écran
                          qui liste les boutons.
                          Réservée à l'administrateur du projet : `submission_url`
                          n'accompagne la ligne que si le lecteur peut éditer le
                          groupe (contrat Syncable de decoupled_toolbox), ce qui
                          évite un appel de rôle par ligne. */}
                      {project._meta?.submissionUrl && (
                        <button
                          type="button"
                          onClick={() => setSettingsProject(project)}
                          className="text-slate-400 hover:text-iris-600 bg-transparent hover:bg-iris-50 p-2 rounded-md transition-all"
                          aria-label={`Configurer le projet ${project.name}`}
                          title={`Configurer le projet ${project.name}`}
                        >
                          <Settings size={18} aria-hidden="true" />
                        </button>
                      )}
                      <button
                        onClick={() => handleArchiveClick(project)}
                        className="text-slate-400 hover:text-sky-600 bg-transparent hover:bg-sky-50 p-2 rounded-md transition-all"
                        aria-label={`Archiver le projet ${project.name}`}
                        title={`Archiver le projet ${project.name}`}
                      >
                        <Archive size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}

              {showArchived && loadingArchived && (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-sm text-slate-400">
                    Chargement des archivés…
                  </td>
                </tr>
              )}

              {showArchived && !loadingArchived && sortedArchivedProjects.map((project) => {
                const methodologyLabel = getMethodologyLabel(project.methodology);
                return (
                  <tr key={`archived-${project.id}`} className="bg-amber-50 hover:bg-amber-100/70 transition-colors group">
                    {/* Left amber rail = unambiguous "archived" state marker (not just lighter). */}
                    <td className="px-6 py-4 whitespace-nowrap border-l-4 border-amber-400">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-slate-100 rounded-lg flex items-center justify-center text-xl grayscale overflow-hidden">
                          {project.pictoImage ? <img src={project.pictoImage} alt="" className="h-full w-full object-cover" /> : <Picto name={project.picto} size={22} />}
                        </div>
                        <div className="ml-4 min-w-0">
                          <div className="flex items-center gap-2">
                            {/* Readable (AA), de-emphasised via colour — never opacity.
                                Linked like a live project: archived means read-only,
                                not unreachable. */}
                            <Link
                              to={`/kanban2?id=${project.id}`}
                              className="text-sm font-semibold text-slate-600 hover:text-iris-600 hover:underline"
                            >
                              {project.name}
                            </Link>
                            <Link
                              to={`/projets/${project.id}`}
                              aria-label={`Dashboard du projet ${project.name}`}
                              title={`Dashboard du projet ${project.name}`}
                              className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-iris-600 hover:underline"
                            >
                              <LayoutDashboard size={14} aria-hidden="true" />
                              <span>Dashboard</span>
                            </Link>
                            <span className="badge badge-warning text-2xs" aria-label={`Projet archivé le ${formatArchiveDate(project.archived)}`}>
                              <Archive size={12} />
                              Archivé le {formatArchiveDate(project.archived)}
                            </span>
                          </div>
                          <div className="text-sm text-slate-500 truncate max-w-xs">{project.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="badge badge-neutral">
                        {methodologyLabel}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {formatDate(project.updatedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {isAdmin && (
                        <button
                          onClick={() => handleUnarchiveClick(project)}
                          className="inline-flex items-center gap-1.5 text-amber-700 hover:text-emerald-700 bg-amber-100/70 hover:bg-emerald-50 px-2.5 py-1.5 rounded-md transition-all font-medium"
                          title="Désarchiver le projet"
                        >
                          <RotateCcw size={16} />
                          <span className="hidden sm:inline">Désarchiver</span>
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          {projects.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-500 text-lg">Aucun projet pour le moment.</p>
              <button
                onClick={handleOpenCreateModal}
                className="btn btn-ghost mt-4"
              >
                Créez votre premier projet
              </button>
            </div>
          )}

          {projects.length > 0 && visibleProjects.length === 0 && visibleArchivedProjects.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-500 text-lg">Aucun projet ne correspond à « {search} ».</p>
              <button
                type="button"
                onClick={() => setSearch('')}
                className="btn btn-ghost mt-4"
              >
                Effacer la recherche
              </button>
            </div>
          )}
        </div>
      </div>

      {pendingArchive && (
        <ConfirmTrashModal
          isOpen
          onConfirm={handleArchiveConfirm}
          onCancel={() => setPendingArchive(null)}
          title={`Archiver le projet « ${pendingArchive.name} » ?`}
          description="Ce projet et toutes ses tâches seront archivés en lecture seule. Vous pourrez les désarchiver à tout moment."
          confirmLabel="Archiver"
          loading={archiving}
          variant="archive"
        />
      )}

      {pendingUnarchive && (
        <ConfirmTrashModal
          isOpen
          onConfirm={handleUnarchiveConfirm}
          onCancel={() => setPendingUnarchive(null)}
          title={`Désarchiver le projet « ${pendingUnarchive.name} » ?`}
          description="Ce projet et toutes ses tâches seront restaurés et redeviendront actifs."
          confirmLabel="Désarchiver"
          loading={unarchiving}
        />
      )}

      {/* La pop-in ne se monte qu'ouverte : elle interroge la matrice de droits
          du projet à l'ouverture (`projectId`), une instance montée en
          permanence rejouerait cette requête à chaque rendu de la liste. */}
      {settingsProject && (
        <ProjectSettingsModal
          isOpen
          onClose={() => setSettingsProject(null)}
          projectId={settingsProject.id}
          title={settingsProject.name}
          description={settingsProject.description || ''}
          picto={settingsProject.picto || ''}
          methodology={settingsProject.methodology || ''}
          account={settingsProject.client || ''}
          methodologyOptions={methodologyOptions}
          accountOptions={accountOptions}
          hosts={settingsProject.hosts || []}
          agentInstance={settingsProject.agentInstance || ''}
          onAccountCreated={(option) => updateGlobalData([...accountOptions, option], 'accountOptions')}
          settings={settingsModalSettings}
          onSave={handleSettingsSave}
          onMembersChanged={() => refetchProjects()}
          users={users}
          taskTypes={taskTypes}
          // Rôle Drupal global, faute de mieux : le rôle de l'utilisateur DANS
          // le projet demanderait un appel par ligne (/api/project/{id}/role).
          // Choix conservateur — le serveur refuse de toute façon une écriture
          // de workflow non autorisée.
          canEditWorkflow={isAdmin}
        />
      )}
    </div>
  );
};

export default ProjectList;
